import { FetchResponse } from '@zapier/identity-fetch';
import has from 'lodash/has';

import {
  CodeStepResponseBody,
  CodeStepResult,
  LoginErrors,
  LoginResult,
  LoginResultType,
  NEW_DEVICE_CONFIRMATION_REASONS,
  NewDeviceChallenge,
  NewDeviceCodeAction,
} from './types';
import {
  ACCOUNT_LOCKED_ERROR,
  CHALLENGE_EXPIRED_MESSAGE,
  EMAIL_VERIFICATION_REQUIRED_ERROR,
  GENERIC_ERROR_MESSAGE,
  MFA_ERROR,
  NEW_DEVICE_CONFIRMATION_REQUIRED_ERROR,
  PENDING_WORKSPACE_ACCESS_ERROR,
} from './constants';
import { urls } from '@zapier/identity-utils';

function hasErrorsOfType(type: string, errors: any) {
  return (
    Array.isArray(errors) && errors.some((error: any) => error.type === type)
  );
}

/**
 * Reads a new_device_confirmation_required error. The UI keys off `reason`, so
 * an unrecognised one becomes `unknown` and keeps the backend's message as
 * the only text to show for it.
 */
function getNewDeviceChallenge(errors: any): NewDeviceChallenge {
  const error =
    errors.find(
      (item: any) => item?.type === NEW_DEVICE_CONFIRMATION_REQUIRED_ERROR
    ) || {};
  return {
    reason: toNewDeviceReason(error.reason),
    message: typeof error.message === 'string' ? error.message : undefined,
  };
}

function toNewDeviceReason(reason: unknown) {
  return (
    NEW_DEVICE_CONFIRMATION_REASONS.find(known => known === reason) || 'unknown'
  );
}

// 403 codes DRF itself answers with (CSRF, session), which aren't challenges
const API_PERMISSION_CODES = [
  'authentication_failed',
  'not_authenticated',
  'permission_denied',
];

/**
 * "Try again in …" from a Retry-After header's seconds, when there is one
 */
function throttledMessage(retryAfter: string | null) {
  const seconds = Number(retryAfter);
  if (!retryAfter || !Number.isFinite(seconds) || seconds <= 0) {
    return 'Too many attempts. Try again in a few minutes.';
  }
  if (seconds < 60) {
    return `Too many attempts. Try again in ${Math.ceil(seconds)} seconds.`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `Too many attempts. Try again in ${minutes} minute${
    minutes === 1 ? '' : 's'
  }.`;
}

/**
 * Reads a response from the sign-in code step's endpoints. Unlike the login
 * API, they use the standard API error format: the reason is `errors[].code`
 * and the fallback copy is `detail`. The UI keys off the reason, so a code
 * this version doesn't know becomes the `unknown` reason.
 */
export function parseCodeStepResponse(
  resp: FetchResponse<CodeStepResponseBody>,
  action: NewDeviceCodeAction
): CodeStepResult {
  if (resp.ok) {
    // A correct code signs the user in; a resend that sent a code has no body
    return action === 'submit'
      ? { type: 'confirmed' }
      : { type: 'challenge', challenge: { reason: 'new_device' } };
  }

  const errors = Array.isArray(resp.body?.errors) ? resp.body.errors : [];
  const error = errors[0] || {};

  // Too many requests from this IP, or a resend inside the cooldown: the step
  // stays as it is
  if (resp.status === 429 || error.code === 'rate_limited') {
    return {
      type: 'error',
      message: throttledMessage(resp.headers?.get('retry-after')),
    };
  }

  if (resp.status === 403) {
    if (error.code === 'challenge_expired') {
      return { type: 'expired', message: CHALLENGE_EXPIRED_MESSAGE };
    }
    if (error.code === MFA_ERROR) {
      return { type: 'redirect', url: urls.MFA_URL };
    }
    if (error.code && !API_PERMISSION_CODES.includes(error.code)) {
      return {
        type: 'challenge',
        challenge: {
          reason: toNewDeviceReason(error.code),
          message: typeof error.detail === 'string' ? error.detail : undefined,
        },
      };
    }
  }

  if (resp.status === 400 || resp.status === 422) {
    const pointers = errors.map(item => item?.source?.pointer);
    if (resp.status === 422 || pointers.includes('captcha')) {
      // Stay on the step; its own captcha is the one to complete
      return {
        type: 'error',
        message: 'Complete the captcha, then try again.',
      };
    }
    if (pointers.includes('device_code')) {
      return { type: 'error', message: 'Enter the code from the email.' };
    }
  }

  return { type: 'error', message: GENERIC_ERROR_MESSAGE };
}

const fieldConversions: Record<string, string> = {
  captcha_required: 'captcha',
};

export function parseLoginErrors(resp: FetchResponse<LoginResult>) {
  const parsedErrors: LoginErrors = {};
  let resultType = LoginResultType.IS_VALID_LOGIN;
  let redirectUrl = '';
  let newDeviceChallenge: NewDeviceChallenge | undefined;

  try {
    const errors = resp.body.errors;

    if (!resp.headers?.get('content-type')?.includes('application/json')) {
      // The server responded with HTML which normally means cookies have
      // been disabled
      parsedErrors.error = 'It looks like you have cookies disabled';
      resultType = LoginResultType.IS_MISSING_CSRF;
      redirectUrl = urls.COOKIE_ERROR_URL;
    }

    if (resp.status === 429) {
      // The server says we are trying to often
      parsedErrors.error =
        'It looks like you are trying to login too often. If you’re having trouble, please contact support.';
      resultType = LoginResultType.TRYING_TOO_OFTEN;
    } else if (resp.status === 401) {
      // The user entered invalid credentials
      resultType = LoginResultType.IS_BAD_CREDENTIALS;
    } else if (resp.status === 403) {
      // 403s carry a typed error in the response body telling us why the
      // user can authenticate but not proceed. Match on the type to decide
      // where to route them; fall through to the cookie/CSRF fallback for
      // an unknown or empty type.
      if (hasErrorsOfType(ACCOUNT_LOCKED_ERROR, errors)) {
        // The account the user is trying to log in to was locked
        parsedErrors.error = 'Your account has been locked.';
        resultType = LoginResultType.IS_LOCKED;
        redirectUrl = urls.ACCOUNT_LOCKED_URL;
      } else if (hasErrorsOfType(MFA_ERROR, errors)) {
        // The credentials were good but MFA is required to fully log in
        resultType = LoginResultType.IS_2FA_REQUIRED;
        redirectUrl = urls.MFA_URL;
      } else if (hasErrorsOfType(PENDING_WORKSPACE_ACCESS_ERROR, errors)) {
        // User authenticated but is only on org-managed HQs without
        // workspace access yet. Backend has already cleared the session;
        // route the user to the pending workspace access page.
        resultType = LoginResultType.IS_PENDING_WORKSPACE_ACCESS;
        redirectUrl = urls.PENDING_WORKSPACE_ACCESS_URL;
      } else if (hasErrorsOfType(EMAIL_VERIFICATION_REQUIRED_ERROR, errors)) {
        // Credentials were valid but the user has not verified their email
        // yet. Route to the verify-email page. The hook layer appends the
        // user's email as a query param (the page requires it).
        resultType = LoginResultType.IS_EMAIL_VERIFICATION_REQUIRED;
        redirectUrl = urls.VERIFY_EMAIL_URL;
      } else if (
        hasErrorsOfType(NEW_DEVICE_CONFIRMATION_REQUIRED_ERROR, errors)
      ) {
        // The password was correct, but this browser isn't trusted for the
        // account yet, so no session was created. The backend emails a
        // sign-in code, which the form asks for instead of redirecting.
        resultType = LoginResultType.IS_NEW_DEVICE_CONFIRMATION_REQUIRED;
        newDeviceChallenge = getNewDeviceChallenge(errors);
      } else {
        // User has cookies disabled and csrf cannot be set
        parsedErrors.error = 'It looks like you have cookies disabled';
        resultType = LoginResultType.IS_MISSING_CSRF;
        redirectUrl = urls.COOKIE_ERROR_URL;
      }
    }

    // See if we need to parse the errors array or not
    if (Array.isArray(errors) && errors.length > 0) {
      const firstError = errors[0];

      // Some errors are returned as a string
      if (firstError && typeof firstError === 'string') {
        parsedErrors.error = firstError;
        // We don't know what type of error this is so set UNKNOWN
        if (resultType === LoginResultType.IS_VALID_LOGIN) {
          resultType = LoginResultType.UNKNOWN_ERROR;
        }
      }

      // Contextual messages like "You are already logged in."
      // @ts-ignore
      if (firstError && typeof firstError === 'object' && firstError?.message) {
        // @ts-ignore
        parsedErrors.error = firstError?.message;
        if (resultType === LoginResultType.IS_VALID_LOGIN) {
          resultType = LoginResultType.UNKNOWN_ERROR;
        }
      }

      // One or more fields has returned an error
      errors.forEach(error => {
        if (typeof error === 'string') {
          return;
        }

        const fields = error?.fields;

        if (fields) {
          fields.forEach(field => {
            const key = fieldConversions[field.key] || field.key;
            // @ts-ignore
            parsedErrors[key] = field.message;
          });
        }
      });
      /**
       * We have an error, but we don't know how to deal with it. So throw
       * a generic error.
       */
      if (Object.keys(parsedErrors).length === 0) {
        throw resp;
      }
    }
  } catch (err) {
    /**
     * Something has blown up, maybe the API is down or the client might
     * be disconnected, or we have an error we cannot parse. In this case
     * send along a generic error.
     */
    parsedErrors.error = GENERIC_ERROR_MESSAGE;
    if (resultType === LoginResultType.IS_VALID_LOGIN) {
      resultType = LoginResultType.UNKNOWN_ERROR;
    }
  }

  if (resp.status === 422 && has(parsedErrors, 'captcha')) {
    resultType = LoginResultType.IS_CAPTCHA_REQUIRED;
  }

  return {
    errors: parsedErrors,
    newDeviceChallenge,
    redirectUrl,
    type: resultType as LoginResultType,
  };
}
